import { type ReactNode } from "react";

import { Colors, fontMedium, fontXSmall } from "~foundations";

import { TextAreaInput, type TextAreaInputProps } from "../TextAreaInput";

/***** Constants *****/
const RADIO_SIZE = 18; // Size of the actual radio input
const RADIO_PADDING = 3; // Padding to make total clickable area 24px (mimic design)
const RADIO_INNER_SIZE = 9; // Half of the radio size for the inner circle

/********* Types ********/
export type RadioInputProps<TValue extends string | number> = {
  /**
   * Label of the radio input.
   */
  label: string;
  /**
   * Value of this radio input.
   */
  value: TValue;

  /** [Optional] Description displayed below the label. */
  description?: ReactNode;

  /**
   * [Optional] Renders a container around the component to emphasize it.
   * @default false
   */
  hasContainer?: boolean;

  /** Callback when the selected option changes */
  onChange: (value: TValue) => void;
  /**
   * Optional text field to display when the radio input is selected.
   * @default undefined
   */
  hasTextField?: TextAreaInputProps;
  isSelected?: boolean;
  /**
   * Visual and interactive state of the input.
   * @default "enabled"
   */
  status?: "enabled" | "readOnly" | "disabled";
  /**
   * Used to group radio inputs.
   * @default undefined
   */
  groupName?: string;
  "data-testid"?: string;
};

/**
 * Component | RadioInput
 *
 * NOTE: Intended for internal use within the RadioList component only.
 * Not exported outside the project as a standalone component.
 *
 * @Figma: https://www.figma.com/design/uJsnuoosxqn26Rot52zCXZ/%F0%9F%A7%B9-Swing-Design-System-v2?node-id=11456-3037&t=wsDIuxOlPRdHgHUZ-4
 */
export function RadioInput<TValue extends string | number>(props: RadioInputProps<TValue>) {
  const {
    label,
    description,
    hasContainer = false,
    value,
    isSelected,
    status = "enabled",
    hasTextField,
    onChange,
    groupName,
    "data-testid": dataTestId,
  } = props;

  const isDisabled = status === "disabled";
  const isReadOnly = status === "readOnly";

  /*** Handlers ***/
  function handleChange() {
    // Do not call when in readOnly or disabled state
    if (status === "enabled") {
      onChange(value);
    }
  }

  /*** Render ***/
  return (
    <label
      data-testid={
        dataTestId ? `${dataTestId}-${value}` : groupName ? `${groupName}-${value}` : undefined
      }
      css={{
        // Layout
        display: "flex",
        alignItems: "flex-start",
        gap: 16,
        padding: "16px 24px",

        // Background & Hover
        transition: "background-color 200ms ease",
        background: Colors.white200,
        ":hover": !isDisabled && !isReadOnly ? { background: Colors.white300 } : {},

        // Text
        ...fontMedium({ color: isDisabled ? "slate300" : "black" }),

        cursor: isDisabled || isReadOnly ? "not-allowed" : "pointer",

        // Border Emphasis
        ...(hasContainer && {
          border: `1px solid ${Colors.blue500}`,
          borderRadius: 8,
        }),
      }}
    >
      {/* Hidden input for accessibility */}
      <input
        name={groupName}
        type="radio"
        value={value}
        checked={isSelected}
        onChange={handleChange}
        disabled={isDisabled}
        readOnly={isReadOnly}
        aria-label={label}
        css={{ opacity: 0, position: "absolute" }}
      />

      {/* Radio Input */}
      <div
        css={{
          width: RADIO_SIZE + RADIO_PADDING * 2, // Total width 24px
          height: RADIO_SIZE + RADIO_PADDING * 2, // Total height 24px
          padding: RADIO_PADDING,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <div
          css={{
            width: RADIO_SIZE,
            height: RADIO_SIZE,
            borderRadius: "50%",
            boxSizing: "border-box",
            border: `2px solid ${isDisabled ? Colors.slate300 : isReadOnly ? Colors.black : Colors.blue500}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            transition: "border-color 0.2s ease",
            position: "relative",
            ...(isSelected && !isDisabled ? { ":hover": { background: Colors.white300 } } : {}),
          }}
        >
          <div
            css={{
              width: RADIO_INNER_SIZE,
              height: RADIO_INNER_SIZE,
              borderRadius: "50%",
              backgroundColor: isSelected
                ? isDisabled
                  ? Colors.slate300
                  : isReadOnly
                    ? Colors.black
                    : Colors.blue500
                : Colors.white200,
              position: "absolute",
              top: "50%",
              left: "50%",
              transition: "transform 280ms cubic-bezier(0.4, 0, 0.2, 1) 0s",
              transform: isSelected
                ? "translate(-50%, -50%) scale3d(1, 1, 1)"
                : "translate(-50%, -50%) scale3d(0, 0, 0)",
            }}
          />
        </div>
      </div>

      {/* Label */}
      <div css={{ width: "100%" }}>
        <p css={{ ...fontMedium(), margin: 0 }}>{label}</p>
        {description && <p css={{ margin: 0, ...fontXSmall() }}>{description}</p>}
        {hasTextField && isSelected && (
          <div css={{ paddingTop: 16 }}>
            <TextAreaInput {...hasTextField} />
          </div>
        )}
      </div>
    </label>
  );
}
